/**
 * Registre des fluides (§9.2) : ce qu'une opération inscrit et ce qu'elle fait
 * au contenu de la bouteille. Mêmes règles pour l'API (contrôle), le web
 * (saisie) et le bilan annuel ; la base les garantit aussi (migration 0196).
 *
 * - charge : fluide mis dans une machine depuis une bouteille ; sa nature
 *   suit le CERFA 15497 (vierge, recyclé, régénéré) ;
 * - récupération : fluide repris d'une machine dans une bouteille de
 *   récupération, destiné au traitement ou conservé pour réutilisation ;
 * - réception : bouteille achetée ou livrée (ou déjà en stock à son
 *   enregistrement : reprise d'inventaire) ;
 * - retour : bouteille rendue au distributeur ou au collecteur avec son
 *   contenu, pour régénération, pour destruction, ou non utilisé (reprise).
 *
 * Une erreur se corrige par une contre-passation : une ligne liée, de mêmes
 * quantités, qui annule l'originale ; la bonne valeur s'inscrit à côté.
 */

/** Natures possibles de chaque opération ; ses clés sont les opérations du registre. */
export const FLUID_NATURES = {
  charge: ['vierge', 'recycle', 'regenere'],
  recuperation: ['traitement', 'reutilisation'],
  reception: ['achat', 'inventaire'],
  retour: ['regeneration', 'destruction', 'reprise'],
} as const;
export type FluidMovementKind = keyof typeof FLUID_NATURES;

/** Opérations faites sur une machine : elles exigent la machine et l'attestation d'aptitude de l'opérateur. */
export type MachineMovementKind = 'charge' | 'recuperation';
export type FluidNature<K extends FluidMovementKind = FluidMovementKind> = (typeof FLUID_NATURES)[K][number];

/** Une opération porte au plus une tonne de fluide, comme une bouteille. */
export const FLUID_MAX_GRAMS = 1_000_000;

/**
 * Heure du geste : saisie après coup (bureau, papier du technicien) jusqu'à
 * 30 jours en arrière ; jamais dans le futur, à quelques minutes d'horloge près.
 */
export const FLUID_BACKDATE_DAYS = 30;
export const FLUID_CLOCK_SKEW_MINUTES = 5;

/** Motif d'une correction du registre : obligatoire, court. */
export const FLUID_REASON_MIN = 3;
export const FLUID_REASON_MAX = 500;

export function isFluidNature<K extends FluidMovementKind>(kind: K, nature: unknown): nature is FluidNature<K> {
  return (FLUID_NATURES[kind] as readonly string[]).includes(nature as string);
}

/**
 * Nature proposée pour une charge ou une récupération selon l'usage de la
 * bouteille. Recharger depuis une bouteille de récupération, c'est réutiliser
 * du fluide recyclé : à confirmer explicitement, et inscrit comme tel.
 */
export function defaultFluidNature(kind: MachineMovementKind, cylinderKind: 'charge' | 'recuperation'): FluidNature {
  if (kind === 'recuperation') return 'traitement';
  return cylinderKind === 'recuperation' ? 'recycle' : 'vierge';
}

/** Grammes que l'opération ajoute (positif) ou retire (négatif) de la bouteille. */
export function cylinderDeltaGrams(movement: {
  kind: string; added_grams: number; recovered_grams: number; stock_grams: number;
}): number {
  switch (movement.kind) {
    case 'charge': return -movement.added_grams;
    case 'recuperation': return movement.recovered_grams;
    case 'reception': return movement.stock_grams;
    case 'retour': return -movement.stock_grams;
    default: return 0;
  }
}

/** Export du registre : au-delà, l'export échoue en le disant plutôt que de tronquer. */
export const FLUID_EXPORT_MAX_ROWS = 20_000;

/**
 * Bilan annuel d'un fluide, en grammes (année civile à l'heure de Paris, selon
 * l'heure du geste ; contre-passations déduites). Les stocks sont ceux des
 * bouteilles de l'entreprise au 1er janvier et au 1er janvier suivant.
 */
export interface FluidBalance {
  code: string;
  gwp: number;
  stock_start_grams: number;
  purchased_grams: number;
  inventory_grams: number;
  charged_grams: number;
  charged_new_equipment_grams: number;
  charged_existing_equipment_grams: number;
  charged_virgin_grams: number;
  charged_recycled_grams: number;
  charged_regenerated_grams: number;
  recovered_grams: number;
  recovered_treatment_grams: number;
  recovered_reuse_grams: number;
  returned_regeneration_grams: number;
  returned_destruction_grams: number;
  returned_unused_grams: number;
  stock_end_grams: number;
  /** Charge de l'année en tonnes équivalent CO₂. */
  teq_co2_charged: number;
}

/**
 * Lignes du bilan, dans l'ordre de lecture (écran et export) : clé du bilan et
 * clé de libellé (`fluides.bilan.lignes`). Les lignes « dont » détaillent la
 * ligne qui les précède.
 */
export const FLUID_BALANCE_LINES = [
  ['stock_start_grams', 'stockDebut'],
  ['purchased_grams', 'achete'],
  ['inventory_grams', 'inventaire'],
  ['charged_grams', 'charge'],
  ['charged_new_equipment_grams', 'chargeNeuves'],
  ['charged_existing_equipment_grams', 'chargeExistantes'],
  ['charged_virgin_grams', 'chargeVierge'],
  ['charged_recycled_grams', 'chargeRecycle'],
  ['charged_regenerated_grams', 'chargeRegenere'],
  ['recovered_grams', 'recupere'],
  ['recovered_treatment_grams', 'recupereTraitement'],
  ['recovered_reuse_grams', 'recupereReutilisation'],
  ['returned_regeneration_grams', 'remisRegeneration'],
  ['returned_destruction_grams', 'remisDestruction'],
  ['returned_unused_grams', 'rendu'],
  ['stock_end_grams', 'stockFin'],
] as const satisfies readonly (readonly [keyof FluidBalance, string])[];
